/* Query and mutation hooks for authentication.
 *
 * Server state lives in TanStack Query and nowhere else — `frontend-react.md`.
 * There is no store holding "the current user" in parallel with this; a second
 * copy is a second thing to invalidate, and the one that gets forgotten is
 * always the one somebody is looking at.
 */

import {
  type CurrentUser,
  requestPasswordResetResponseSchema,
  resetPasswordResponseSchema,
  signInResponseSchema,
  signUpResponseSchema,
  verifyEmailResponseSchema,
} from '@eventuley/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError, request } from '../../../shared/api/client.js';

/** Everything about the signed-in User hangs off this one key. */
export const CURRENT_USER_KEY = ['auth', 'me'] as const;

/**
 * Who is signed in, or null.
 *
 * A 401 is a legitimate answer here, not a failure: it means nobody is signed
 * in, which is the normal state of the sign-in page. Treating it as an error
 * would make TanStack Query retry it and would light up an error boundary on
 * the one screen where it is expected.
 */
export function useCurrentUser() {
  return useQuery({
    queryKey: CURRENT_USER_KEY,
    queryFn: async (): Promise<CurrentUser | null> => {
      try {
        const { user } = await request('/auth/me', { schema: signInResponseSchema });
        return user;
      } catch (error) {
        if (error instanceof ApiError && error.status === 401) return null;
        throw error;
      }
    },
    /* Retrying a "no, you are not signed in" answer accomplishes nothing. */
    retry: false,
    staleTime: 30_000,
  });
}

export interface SignUpValues {
  readonly email: string;
  readonly displayName: string;
  readonly password: string;
}

export function useSignUp() {
  return useMutation({
    mutationFn: (values: SignUpValues) =>
      request('/auth/sign-up', {
        method: 'POST',
        schema: signUpResponseSchema,
        body: {
          ...values,
          /* The prototype's model, kept: the sentence above the button states
           * that continuing accepts both documents, and pressing it is the
           * acceptance. That is a contract basis, not consent — consent would
           * need an unticked box, and `security.md` is specific about which is
           * which. The server records the version of each regardless. */
          acceptsTerms: true,
          acceptsDpa: true,
        },
      }),
  });
}

export interface SignInValues {
  readonly email: string;
  readonly password: string;
}

export function useSignIn() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (values: SignInValues) =>
      request('/auth/sign-in', {
        method: 'POST',
        schema: signInResponseSchema,
        body: values,
      }),
    onSuccess: ({ user }) => {
      /* Seeded rather than refetched: the sign-in response already carries
         exactly what `/auth/me` would return, so asking again is a round trip
         for an answer we hold. */
      queryClient.setQueryData(CURRENT_USER_KEY, user);
    },
  });
}

export function useSignOut() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => request('/auth/sign-out', { method: 'POST' }),
    onSuccess: async () => {
      /* Cleared, not just invalidated. Invalidating would refetch and briefly
       * hold the old User while the request is in flight, which on a shared
       * machine is somebody else's name on the screen after they signed out. */
      queryClient.setQueryData(CURRENT_USER_KEY, null);
      await queryClient.invalidateQueries();
    },
  });
}

export function useVerifyEmail() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (token: string) =>
      request('/auth/verify-email', {
        method: 'POST',
        schema: verifyEmailResponseSchema,
        body: { token },
      }),
    onSuccess: async () => {
      /* Verification changes `emailVerified`, which gates progression. */
      await queryClient.invalidateQueries({ queryKey: CURRENT_USER_KEY });
    },
  });
}

export function useRequestPasswordReset() {
  return useMutation({
    mutationFn: (values: { email: string }) =>
      request('/auth/request-password-reset', {
        method: 'POST',
        schema: requestPasswordResetResponseSchema,
        body: values,
      }),
  });
}

export function useResetPassword() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (values: { token: string; password: string }) =>
      request('/auth/reset-password', {
        method: 'POST',
        schema: resetPasswordResponseSchema,
        body: values,
      }),
    onSuccess: async () => {
      /* A reset ends every session, including any this browser was holding, so
       * the cached User is now wrong. Cleared rather than invalidated: a
       * refetch would briefly keep showing somebody who is no longer signed in. */
      queryClient.setQueryData(CURRENT_USER_KEY, null);
      await queryClient.invalidateQueries();
    },
  });
}
